import ReceiptWizardLayout from './ReceiptWizardLayout';
import React, {useEffect, useMemo, useState} from 'react';
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useNavigate} from "react-router";
import toast from "react-hot-toast";
import {
  Alert,
  AlertTitle,
  Avatar,
  Collapse,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  DeleteOutlineRounded,
  ExpandMoreRounded,
  ReceiptLongRounded,
} from "@mui/icons-material";
import {UserResponse} from "../../../services/users/userModel";
import {ErrorResponse} from "../../../services/models/error-response";
import {Receipt} from "../../../services/receipt/receiptModel";
import {GroupGuest} from "../../../services/groups/groupModel";
import {
  canonicalParticipantKey, isGuest, ParticipantKey, participantKey, participantKeys,
  participantName, SplitParticipant,
} from "../../../services/groups/participants";
import {api} from "../../../services";
import {
  buildCreateRequest,
  computeSplit,
  createRow,
  isCurrencySupported,
  lineTotalCents,
  ReviewRow,
  receiptEditError,
  receiptToRows,
  SplitMode,
  SUPPORTED_CURRENCY,
  validateRows,
} from "./reviewModel";
import {formatCentsAsCurrency, formatCurrency} from "../../../utils/format";
import ScanProgress from "./ScanProgress";
import ReceiptReviewWarnings from "./ReceiptReviewWarnings";
import SplitSummary from "./SplitSummary";
import QuantityAssignment from "./QuantityAssignment";
import useAuth from '../../../hooks/auth/useAuth';
import {AVATAR_COLORS, avatarSrc, initials} from '../../../utils/avatar';
import classes from './ReceiptReview.module.scss';

interface EditRow extends ReviewRow {
  quantityText: string;
  priceText: string;
}

export interface ReceiptReviewDraft {
  name: string;
  groupName: string;
  rows: EditRow[];
  mode: SplitMode;
  participantIds: ParticipantKey[];
  guests?: GroupGuest[];
}

interface ReceiptReviewProps {
  practice?: boolean;
  stage?: 'items' | 'split';
  onContinue?: (draft: ReceiptReviewDraft) => void;
  initialRows: ReviewRow[];
  initialDraft?: ReceiptReviewDraft;
  initialMode?: SplitMode;
  receiptName: string;
  groupName: string;
  members: UserResponse[];
  guests?: GroupGuest[];
  editingReceipt?: Receipt;
  onSaved?: (receipt: Receipt) => void;
  currency?: string | null;
  scanId?: string;
  ocrTotal?: number | null;
  warnings?: string[];
  onBack?: (draft: ReceiptReviewDraft) => void;
  backLabel?: string;
  progressSteps?: string[];
  reviewStepIndex?: number;
}

function toEditRow(row: ReviewRow): EditRow {
  return {...row, quantityText: String(row.quantity), priceText: String(row.price)};
}

function parseNum(text: string): number {
  const value = parseFloat(text.replace(',', '.'));
  return Number.isFinite(value) ? value : 0;
}

function stripEdit(rows: EditRow[]): ReviewRow[] {
  return rows.map(({quantityText, priceText, ...rest}) => ({
    ...rest,
    quantity: parseNum(quantityText),
    price: parseNum(priceText),
  }));
}

export function draftRows(draft: ReceiptReviewDraft): ReviewRow[] {
  return stripEdit(draft.rows);
}

const ReceiptReview = ({
  practice = false,
  stage,
  onContinue,
  initialRows,
  initialDraft,
  initialMode = 'equal',
  receiptName,
  groupName,
  members,
  guests = [],
  editingReceipt,
  onSaved,
  currency: suppliedCurrency,
  scanId,
  ocrTotal,
  warnings = [],
  onBack,
  backLabel = 'Back to capture',
  progressSteps,
  reviewStepIndex = 0,
}: ReceiptReviewProps) => {
  const navigate = useNavigate();
  const {user} = useAuth();
  const [guestsExpanded, setGuestsExpanded] = useState(false);
  const queryClient = useQueryClient();
  const currency = editingReceipt ? editingReceipt.currency : suppliedCurrency;
  const [localGuests, setLocalGuests] = useState<GroupGuest[]>(() =>
    initialDraft?.groupName === groupName ? initialDraft.guests ?? [] : []);
  const participants = useMemo(() => {
    const all: SplitParticipant[] = [...members, ...guests, ...localGuests];
    editingReceipt?.entries.forEach(entry => {
      if (entry.user) all.push(entry.user);
      else if (entry.guest) all.push(entry.guest);
    });
    return Array.from(new Map(all.map(participant => [participantKey(participant), participant])).values());
  }, [members, guests, localGuests, editingReceipt]);
  const [name, setName] = useState(editingReceipt?.name ?? receiptName);
  const [rows, setRows] = useState<EditRow[]>(() => editingReceipt
    ? receiptToRows(editingReceipt).map(toEditRow)
    : initialDraft
      ? initialDraft.rows.map(row => ({
        ...row, memberIds: participantKeys(row.memberIds)
          .filter(key => participants.some(member => participantKey(member) === key)),
      }))
      : initialRows.map(toEditRow));
  const [mode, setMode] = useState<SplitMode>(() => stage === 'split' && groupName ? 'items' : editingReceipt?.splitMode ?? initialDraft?.mode ?? initialMode);
  const [participantIds, setParticipantIds] = useState<string[]>(() =>
    editingReceipt?.participantKeys ? [...editingReceipt.participantKeys] :
    initialDraft?.groupName === groupName
      ? participantKeys(initialDraft.participantIds)
      : participants.map(participantKey));
  const [savedReceipt, setSavedReceipt] = useState<Receipt | null>(null);
  const [saveError, setSaveError] = useState<string>();
  const [guestName, setGuestName] = useState('');
  const [guestError, setGuestError] = useState<string>();
  const [guestSaved, setGuestSaved] = useState(false);
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    if (!editingReceipt?.editableUntil) return;
    const remaining = Date.parse(editingReceipt.editableUntil) - Date.now();
    if (!Number.isFinite(remaining) || (remaining <= 0 && now >= Date.parse(editingReceipt.editableUntil))) return;
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(0, Math.min(remaining, 2147483647)));
    return () => window.clearTimeout(timer);
  }, [editingReceipt?.editableUntil, now]);

  const cleanRows = useMemo(() => stripEdit(rows), [rows]);
  const activeKeys = useMemo(() => mode === 'equal' ? participantIds
    : participantKeys([...participantIds, ...cleanRows.flatMap(row => row.allocations ? row.allocations.flatMap(portion => portion.participantKeys) : row.memberIds)]),
  [mode, participantIds, cleanRows]);
  const activeMembers = useMemo(
    () => activeKeys.flatMap(key => {
      const member = participants.find(participant => participantKey(participant) === key);
      return member ? [member] : [];
    }),
    [participants, activeKeys]
  );
  const editError = editingReceipt ? receiptEditError(editingReceipt, now) : undefined;
  const missingParticipants = activeKeys.some(key => !participants.some(member => participantKey(member) === key));

  const split = useMemo(
    () => computeSplit(cleanRows, activeMembers, mode),
    [cleanRows, activeMembers, mode]
  );

  const guestMutation = useMutation<GroupGuest, ErrorResponse>(
    async () => {
      if (practice) throw new Error('Guests are not saved during practice.');
      const trimmed = guestName.trim();
      if (!groupName || !trimmed || trimmed.length > 80) throw new Error('Enter a guest name between 1 and 80 characters.');
      const guest = await api.groups.addGuest({groupName, name: trimmed});
      if (!guest?.id || !guest.name) {
        throw new Error(guest && 'message' in guest && typeof guest.message === 'string'
          ? guest.message : 'Could not add the guest.');
      }
      return guest;
    },
    {
      onSuccess: guest => {
        setLocalGuests(previous => [...previous.filter(item => item.id !== guest.id), guest]);
        setParticipantIds(previous => participantKeys([...previous, participantKey(guest)]));
        setGuestName('');
        setGuestError(undefined);
        setGuestSaved(true);
        queryClient.invalidateQueries(['groups']);
        queryClient.invalidateQueries(['groupDetails', groupName]);
        toast.success('Saved to this group for future bills');
      },
      onError: error => setGuestError(error.message || 'Could not add the guest. Try again.'),
    }
  );

  const saveMutation = useMutation<Receipt & Partial<ErrorResponse>, ErrorResponse>(
    () => {
      if (practice) throw new Error('Receipts are not saved during practice.');
      setSaveError(undefined);
      if (editingReceipt) {
        const error = receiptEditError(editingReceipt);
        if (error) throw new Error(error);
      }
      const request = buildCreateRequest({
        name,
        groupName,
        rows: cleanRows,
        members: activeMembers,
        mode,
        currency,
        scanId: editingReceipt ? undefined : scanId,
      });
      if (editingReceipt) {
        if (editingReceipt.id == null || editingReceipt.version == null) {
          throw new Error('Reload this receipt before saving: its ID or version is missing.');
        }
        return api.receipt.update(editingReceipt.id, {...request, version: editingReceipt.version});
      }
      return api.receipt.create(request);
    },
    {
      onSuccess: (data) => {
        if (data?.message && !data?.entries) {
          toast.error(data.message);
          setSaveError(data.message);
          return;
        }
        toast.success('Receipt saved and split');
        if (onSaved) onSaved(data);
        else setSavedReceipt(data);
      },
      onError: err => {
        const message = err?.message || 'Could not save the receipt. Your changes are still here.';
        setSaveError(message);
        toast.error(message);
      },
    }
  );

  const updateRow = (id: number, patch: Partial<EditRow>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? {...r, ...patch} : r)));

  const toggleRowMember = (rowId: number, memberId: string) =>
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const keys = participantKeys(r.memberIds);
        const has = keys.includes(memberId);
        return {
          ...r,
          memberIds: has ? keys.filter((m) => m !== memberId) : [...keys, memberId],
        };
      })
    );

  const addRow = () => setRows((prev) => [...prev, toEditRow(createRow())]);
  const removeRow = (id: number) => setRows((prev) => prev.filter((r) => r.id !== id));

  const toggleParticipant = (id: string) => {
    const removing = participantIds.includes(id);
    setParticipantIds(previous => removing ? previous.filter(key => key !== id) : [...previous, id]);
    if (stage === 'split' && removing) {
      setRows(previous => previous.map(row => ({...row,
        memberIds: row.memberIds.filter(key => canonicalParticipantKey(key) !== id),
      })));
    }
  };

  const nameValid = name.trim().length > 0 && name.length <= 60;
  const hasRows = cleanRows.length > 0;
  const rowErrors = useMemo(() => validateRows(cleanRows), [cleanRows]);
  const currencyBlocked = !isCurrencySupported(currency);
  const canSave =
    nameValid &&
    hasRows &&
    split.balanced &&
    rowErrors.length === 0 &&
    !currencyBlocked &&
    !editError &&
    !missingParticipants &&
    !guestMutation.isLoading &&
    !saveMutation.isLoading;

  const totalMismatch =
    ocrTotal != null && Math.abs(ocrTotal - split.totalCents / 100) > 0.011;

  if (savedReceipt) {
    return (
      <SplitSummary
        receipt={savedReceipt}
        currency={currency}
        onOpen={() => savedReceipt.id != null && navigate(`/receipt/${savedReceipt.id}`)}
        onDone={() => navigate('/home')}
      />
    );
  }

  if (stage === 'split' || (stage !== 'items' && rows.some(row => row.allocations != null))) {
    return <QuantityAssignment
      rows={cleanRows.map(row => ({...row, allocations: row.allocations ?? (row.memberIds.length
        ? [{quantity: row.quantity, participantKeys: participantKeys(row.memberIds)}] : [])}))}
      participants={participants}
      currentUserId={user?.id}
      currency={currency}
      onChange={(rowId, allocations) => updateRow(rowId, {allocations, memberIds: participantKeys(allocations.flatMap(portion => portion.participantKeys))})}
      onBack={onBack ? () => onBack({name, groupName, rows, mode, participantIds, guests: participants.filter(isGuest)}) : undefined}
      onSave={() => saveMutation.mutate()}
      canSave={canSave}
      saving={saveMutation.isLoading}
      busy={guestMutation.isLoading}
      editingName={editingReceipt ? <TextField label="Receipt name" value={name} onChange={event => setName(event.target.value)} inputProps={{maxLength: 60}} fullWidth/> : undefined}
      errors={<>
        {saveError && <Alert severity="error">{saveError}</Alert>}
        {editError && <Alert severity="error">{editError}</Alert>}
        {currencyBlocked && <Alert severity="error">Only EUR receipts can be saved.</Alert>}
        {missingParticipants && <Alert severity="error">Remove assignments to unavailable participants before saving.</Alert>}
        {rowErrors.length > 0 && <Alert severity="error">{rowErrors.join(' ')}</Alert>}
      </>}
      guests={groupName ? <>
        <Button className={classes.guestToggle} onClick={() => setGuestsExpanded(value => !value)}
          aria-expanded={guestsExpanded} aria-controls="receipt-guests" endIcon={<ExpandMoreRounded/>}>Add someone without an account</Button>
        <Collapse in={guestsExpanded} unmountOnExit><Stack id="receipt-guests" spacing={1} className={classes.guestForm}>
          <TextField label="Guest name" value={guestName} onChange={event => {setGuestName(event.target.value); setGuestError(undefined);}}
            inputProps={{maxLength: 80}} disabled={guestMutation.isLoading || saveMutation.isLoading}
            error={!!guestError} helperText={guestError || 'Guests are saved to this group for future bills.'}/>
          <Button startIcon={<AddRounded/>} disabled={!guestName.trim() || guestName.trim().length > 80 || guestMutation.isLoading || saveMutation.isLoading}
            onClick={() => guestMutation.mutate()}>{guestMutation.isLoading ? 'Adding guest…' : 'Add guest'}</Button>
          {guestSaved && <Typography role="status">Saved to this group for future bills</Typography>}
        </Stack></Collapse>
      </> : null}
    />;
  }

  return (
    <ReceiptWizardLayout tourStage={stage === 'items' ? 'review' : undefined} title={stage === 'items' ? 'Review items' : 'Review & split'}
      onBack={onBack ? () => onBack({name, groupName, rows, mode, participantIds, guests: participants.filter(isGuest)}) : undefined}
      backLabel={backLabel} backDisabled={saveMutation.isLoading || guestMutation.isLoading}>
    <Stack spacing={3} sx={{
      '& .MuiAlert-standardWarning': {bgcolor: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d', borderRadius: '16px', '& .MuiAlert-icon': {color: '#d99700'}},
      '& .MuiCard-root': {borderRadius: '20px', borderColor: '#eceef2', boxShadow: '0 2px 4px #1b203514'},
      '& .MuiToggleButton-root.Mui-selected, & .MuiChip-colorPrimary': {bgcolor: '#1b2035', color: '#fff'},
    }}>
      {progressSteps && !stage && (
        <ScanProgress steps={progressSteps} activeStep={reviewStepIndex}/>
      )}

      {stage === 'items' && <Card sx={{p: 2.5}}>
        <Typography variant="h6">{name}</Typography>
        <Typography variant="body2" color="text.secondary">{currency || SUPPORTED_CURRENCY} · Check the products and amounts</Typography>
      </Card>}
      <ReceiptReviewWarnings warnings={warnings}/>

      {!stage && <TextField
        label="Receipt name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        error={!nameValid}
        helperText={!nameValid ? 'Give the receipt a name' : (groupName ? 'Shared group receipt' : 'Private receipt — only you can access it')}
        inputProps={{maxLength: 60, 'aria-label': 'Receipt name'}}
        fullWidth
      />}

      {stage !== 'items' && (groupName || !stage) && <>
      {stage !== 'split' && <Box>
        <Typography variant="subtitle2" component="div" id="split-mode-label" gutterBottom>
          How do you want to split it?
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={mode}
          onChange={(_, v) => v && setMode(v)}
          aria-labelledby="split-mode-label"
          fullWidth
        >
          <ToggleButton value="equal" sx={{minHeight: 44}}>Split equally</ToggleButton>
          <ToggleButton value="items" sx={{minHeight: 44}}>Assign items</ToggleButton>
        </ToggleButtonGroup>
      </Box>}

      {(mode === 'equal' || stage === 'split') && (
        <Card className={classes.people} variant="outlined">
          <Typography variant="h6" component="h2" gutterBottom>Who&apos;s sharing?</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {participants.map((m, index) => (
              <Chip
                key={participantKey(m)}
                avatar={<Avatar aria-hidden="true" src={!isGuest(m) ? avatarSrc(m) : undefined} sx={{bgcolor: AVATAR_COLORS[index % AVATAR_COLORS.length]}}>{initials(participantName(m))}</Avatar>}
                label={<>{participantName(m)}{stage === 'split' && !isGuest(m) && m.id === user?.id && <small> (you)</small>}{isGuest(m) && <strong> · Guest</strong>}</>}
                onClick={() => toggleParticipant(participantKey(m))}
                aria-pressed={participantIds.includes(participantKey(m))}
                color={participantIds.includes(participantKey(m)) ? 'primary' : 'default'}
                variant={participantIds.includes(participantKey(m)) ? 'filled' : 'outlined'}
                sx={{minHeight: 48, borderRadius: '14px', px: 0.5, fontWeight: 700, '& .MuiChip-avatar': {color: '#fff'}}}
              />
            ))}
          </Stack>
          {activeMembers.length === 0 && (
            <Typography variant="body2" color="error" role="alert" sx={{mt: 1}}>
              Select at least one person to share the bill.
            </Typography>
          )}
        </Card>
      )}

      {groupName && <>
        {stage === 'split' && <Button className={classes.guestToggle} onClick={() => setGuestsExpanded(value => !value)}
          aria-expanded={guestsExpanded} aria-controls="receipt-guests" endIcon={<ExpandMoreRounded/>}>Add someone without an account</Button>}
        <Collapse in={stage !== 'split' || guestsExpanded} unmountOnExit>
        <Stack id="receipt-guests" spacing={1} className={classes.guestForm}>
        <TextField
          label="Guest name"
          value={guestName}
          onChange={event => {
            setGuestName(event.target.value);
            setGuestError(undefined);
            setGuestSaved(false);
          }}
          disabled={!groupName || guestMutation.isLoading || saveMutation.isLoading}
          inputProps={{maxLength: 80}}
          error={!!guestError || guestName.length > 80}
          helperText={guestError || (guestName.length > 80 ? 'Use at most 80 characters.' : 'Add a named guest without an account or email.')}
        />
        <Button
          onClick={() => guestMutation.mutate()}
          disabled={!groupName || !guestName.trim() || guestName.trim().length > 80 || guestMutation.isLoading || saveMutation.isLoading}
          startIcon={<AddRounded/>}
        >{guestMutation.isLoading ? 'Adding guest…' : 'Add guest'}</Button>
        {guestSaved && <Typography role="status" variant="body2">Saved to this group for future bills</Typography>}
      </Stack>
      </Collapse></>}
      </>}

      <Typography variant="h6">{`Items (${rows.length})`}</Typography>
      <Stack spacing={1.5}>
        {rows.map((row) => (
          <Card key={row.id} variant="outlined" sx={{p: 1.5}}>
            <Stack spacing={1}>
              <>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label="Item"
                  value={row.name}
                  onChange={(e) => updateRow(row.id, {name: e.target.value})}
                  fullWidth
                  size="small"
                />
                <IconButton
                  aria-label={`Remove ${row.name || 'item'}`}
                  onClick={() => removeRow(row.id)}
                  sx={{minWidth: 44, minHeight: 44}}
                >
                  <DeleteOutlineRounded/>
                </IconButton>
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Qty"
                  value={row.quantityText}
                  onChange={(e) => updateRow(row.id, {quantityText: e.target.value})}
                  inputProps={{inputMode: 'decimal', 'aria-label': 'Quantity'}}
                  size="small"
                  sx={{width: 80, minWidth: 0}}
                />
                <TextField
                  label="Unit price"
                  value={row.priceText}
                  onChange={(e) => updateRow(row.id, {priceText: e.target.value})}
                  inputProps={{inputMode: 'decimal', 'aria-label': 'Unit price'}}
                  size="small"
                  sx={{width: 108, minWidth: 0}}
                />
                <Box sx={{flexGrow: 1, textAlign: 'right', alignSelf: 'center'}}>
                  <Typography variant="body2" color="text.secondary">Line</Typography>
                  <Typography variant="subtitle2">
                    {formatCentsAsCurrency(lineTotalCents({
                      price: parseNum(row.priceText),
                      quantity: parseNum(row.quantityText),
                    }), currency)}
                  </Typography>
                </Box>
              </Stack>
              </>
              {stage !== 'items' && mode === 'items' && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Shared by</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{mt: 0.5}}>
                    {participants.filter(member => stage !== 'split' || participantIds.includes(participantKey(member))).map((m) => (
                      stage === 'split' ? <button type="button" role="checkbox"
                        key={participantKey(m)} className={classes.assignment}
                        aria-checked={row.memberIds.some(key => canonicalParticipantKey(key) === participantKey(m))}
                        aria-label={`Assign ${row.name || 'item'} to ${participantName(m)}${isGuest(m) ? ' (Guest)' : ''}`}
                        onClick={() => toggleRowMember(row.id, participantKey(m))}>
                        {participantName(m)}{isGuest(m) && <small> · Guest</small>}
                      </button> : <FormControlLabel
                        key={participantKey(m)}
                        sx={stage === 'split' ? {
                          m: 0, px: 1, border: '2px solid #e5e7eb', borderRadius: '14px',
                          '&:has(input:checked)': {bgcolor: '#1b2035', borderColor: '#1b2035', color: '#fff'},
                          '& .MuiCheckbox-root': {color: 'inherit'},
                        } : {mr: 1}}
                        control={
                          <Checkbox
                            size="small"
                            checked={row.memberIds.some(key => canonicalParticipantKey(key) === participantKey(m))}
                            onChange={() => toggleRowMember(row.id, participantKey(m))}
                            inputProps={{'aria-label': `Assign ${row.name || 'item'} to ${participantName(m)}${isGuest(m) ? ' (Guest)' : ''}`}}
                          />
                        }
                        label={<Typography variant="body2">{participantName(m)}{isGuest(m) && <strong> · Guest</strong>}</Typography>}
                      />
                    ))}
                  </Stack>
                  {stage === 'split' && participantIds.length > 1 && <Button size="small"
                    onClick={() => updateRow(row.id, {memberIds: [...participantIds]})}
                    sx={{mt: 1, textTransform: 'none'}} aria-label={`Share ${row.name || 'item'} with everyone`}>
                    Share this item with everyone
                  </Button>}
                  {row.memberIds.length === 0 && (
                    <Typography variant="caption" color="warning.main">Not assigned yet</Typography>
                  )}
                </Box>
              )}
            </Stack>
          </Card>
        ))}
        <Button
          startIcon={<AddRounded/>}
          onClick={addRow}
          variant="outlined"
          sx={{minHeight: 44, alignSelf: 'flex-start'}}
        >
          Add item
        </Button>
      </Stack>

      <Divider/>

      <Card variant="outlined" sx={{p: 2}} aria-live="polite">
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="subtitle1">Receipt total</Typography>
          <Typography variant="subtitle1" fontWeight={700}>
            {formatCentsAsCurrency(split.totalCents, currency)}
          </Typography>
        </Stack>
        {ocrTotal != null && (
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Scanned total</Typography>
            <Typography variant="body2" color={totalMismatch ? 'warning.main' : 'text.secondary'}>
              {formatCurrency(ocrTotal, currency)}
            </Typography>
          </Stack>
        )}
        {totalMismatch && (
          <Alert severity="warning" sx={{mt: 1.5}}>
            {formatCentsAsCurrency(Math.abs(Math.round((ocrTotal ?? 0) * 100) - split.totalCents), currency)} difference — check the items above against the printed receipt.
            Your items don&apos;t match the scanned total — double-check the prices.
          </Alert>
        )}

        {stage !== 'items' && <>
        <Divider sx={{my: 1.5}}/>
        <Typography variant="subtitle1" fontWeight={700} sx={{mb: 1}}>Each person&apos;s share</Typography>

        <Stack spacing={0.5}>
          {split.perMember.map((s) => (
            <Stack key={participantKey(s.participant)} direction="row" justifyContent="space-between">
              <Typography variant="body2">{participantName(s.participant)}{isGuest(s.participant) && <strong> · Guest</strong>}</Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatCentsAsCurrency(s.amountCents, currency)}
              </Typography>
            </Stack>
          ))}
        </Stack>

        {split.unassignedRowIds.length > 0 && (
          <Alert severity="warning" sx={{mt: 1}} role="alert">
            {split.unassignedRowIds.length} item(s) aren&apos;t assigned to anyone yet.
          </Alert>
        )}
        </>}
      </Card>

      {stage !== 'items' && split.perMember.some(summary => summary.guest && summary.amountCents !== 0) && (
        <Alert severity="info">Guests have no account or email. Anyone with a public receipt link can see their names and shares.</Alert>
      )}
      {editError && <Alert severity="error">{editError}</Alert>}
      {missingParticipants && <Alert severity="error">Some selected participants are unavailable. Reload the group before saving.</Alert>}
      {saveError && <Alert severity="error">{saveError}</Alert>}

      {currencyBlocked && (
        <Alert severity="error" role="alert">
          This receipt was detected in <strong>{currency}</strong>. PayMe can only
          save receipts in {SUPPORTED_CURRENCY} for now, so it can&apos;t be stored
          as-is. Re-enter the amounts in {SUPPORTED_CURRENCY} to continue.
        </Alert>
      )}

      {!currencyBlocked && rowErrors.length > 0 && (
        <Alert severity="warning" role="status">
          <AlertTitle>Fix these before saving</AlertTitle>
          <ul style={{margin: 0, paddingLeft: '1.2rem'}}>
            {rowErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Button
        variant="contained"
        size="large"
        startIcon={<ReceiptLongRounded/>}
        disabled={stage === 'items' ? !hasRows || rowErrors.length > 0 || currencyBlocked : !canSave}
        data-tour="review-continue"
        onClick={() => stage === 'items'
          ? onContinue?.({name, groupName, rows, mode, participantIds, guests: participants.filter(isGuest)})
          : saveMutation.mutate()}
        sx={{minHeight: 52}}
      >
        {stage === 'items' ? 'Looks good to me' : saveMutation.isLoading ? 'Saving…' : editingReceipt ? 'Save changes' : 'Save receipt'}
      </Button>
    </Stack>
    </ReceiptWizardLayout>
  );
};

export default ReceiptReview;
